import { Component } from '@theme/component';
import { ThemeEvents } from '@theme/events';

class PersonalisationComponent extends Component {
  refs = {};
  #abortController = new AbortController();

  connectedCallback() {
    super.connectedCallback();

    const { signal } = this.#abortController;

    this.addEventListener('click', this.#handleClick, { signal });
    this.refs.messageInput?.addEventListener('input', this.#handleMessageInput, { signal });

    const target = this.closest('.shopify-section, dialog, product-card');
    target?.addEventListener(ThemeEvents.variantUpdate, this.#handleVariantUpdate, { signal });
    target?.addEventListener(ThemeEvents.cartAdd, this.#handleCartAdd, { signal });

    this.#render();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    this.#abortController.abort();
  }

  get #optionButtons() {
    return Array.from(this.querySelectorAll('[data-personalisation-option-button]'));
  }

  get #charmButtons() {
    return this.#optionButtons.filter((button) => button.dataset.optionType === 'charm');
  }

  get #gemstoneButtons() {
    return this.#optionButtons.filter((button) => button.dataset.optionType === 'gemstone');
  }

  get #selectedCharmButton() {
    return this.#charmButtons.find((button) => button.dataset.selected === 'true') ?? null;
  }

  get #selectedGemstoneButton() {
    return this.#gemstoneButtons.find((button) => button.dataset.selected === 'true') ?? null;
  }

  get #messageValue() {
    return this.refs.messageInput?.value?.trim() ?? '';
  }

  get #form() {
    const formId = this.dataset.formId;

    if (formId) {
      const form = document.getElementById(formId);
      if (form instanceof HTMLFormElement) return form;
    }

    const closestForm = this.closest('form');
    return closestForm instanceof HTMLFormElement ? closestForm : null;
  }

  get #messageIsValid() {
    if (!this.refs.messageInput || this.refs.messageInput.disabled) return true;

    const value = this.#messageValue;
    if (!value) return true;

    return /^[A-Za-z0-9 .&]+$/.test(value);
  }

  #handleClick = (event) => {
    const submitButton = event.target instanceof HTMLElement ? event.target.closest('[data-personalisation-submit]') : null;
    if (submitButton instanceof HTMLButtonElement) {
      this.#setSubmitMode(true);
      return;
    }

    const button = event.target instanceof HTMLElement ? event.target.closest('[data-personalisation-option-button]') : null;
    if (!(button instanceof HTMLButtonElement)) return;

    const isSelected = button.dataset.selected === 'true';
    const optionType = button.dataset.optionType;
    const buttons = optionType === 'charm' ? this.#charmButtons : this.#gemstoneButtons;

    buttons.forEach((optionButton) => {
      this.#setButtonSelected(optionButton, false);
    });

    this.#setButtonSelected(button, !isSelected);
    this.#render();
  };

  #handleMessageInput = () => {
    if (!this.refs.messageInput) return;

    const sanitizedValue = this.refs.messageInput.value.replace(/[^A-Za-z0-9 !"'(),\-./:;?&@#]/g, '').slice(0, 8);
    if (sanitizedValue !== this.refs.messageInput.value) {
      this.refs.messageInput.value = sanitizedValue;
    }

    this.#render();
  };

  #handleVariantUpdate = (event) => {
    const image = event.detail.resource?.featured_media?.preview_image?.src;
    if (!image) return;

    this.dataset.defaultPreviewImage = `${image}&width=900`;

    if (!this.#selectedCharmButton && !this.#selectedGemstoneButton) {
      this.#renderPreview(this.dataset.defaultPreviewImage, this.dataset.defaultPreviewAlt || '');
    }
  };

  #handleCartAdd = (event) => {
    if (event.detail.data?.source !== 'product-form-component') return;

    this.closest('dialog-component')?.closeDialog?.();
  };

  #setButtonSelected(button, isSelected) {
    button.dataset.selected = isSelected ? 'true' : 'false';
    button.setAttribute('aria-pressed', isSelected ? 'true' : 'false');
    button.classList.toggle('border-black', isSelected);
    button.classList.toggle('bg-secondary', isSelected);
    button.classList.toggle('border-gray-300', !isSelected);
    button.classList.toggle('bg-white', !isSelected);
  }

  #setSubmitMode(isPersonalisationSubmit) {
    const submitModeInput = this.#form?.querySelector('[data-personalisation-submit-mode]');

    if (submitModeInput instanceof HTMLInputElement) {
      submitModeInput.value = isPersonalisationSubmit ? 'true' : 'false';
    }
  }

  #render() {
    const selectedCharmButton = this.#selectedCharmButton;
    const selectedGemstoneButton = this.#selectedGemstoneButton;
    const selectedCharmId = selectedCharmButton?.dataset.variantId ?? '';
    const selectedGemstoneId = selectedGemstoneButton?.dataset.variantId ?? '';
    const showEngraving = this.dataset.showEngraving === 'true';
    const hasCharmSelection = Boolean(selectedCharmId);
    const messageInput = this.refs.messageInput;
    const messageError = this.refs.messageError;

    if (messageInput) {
      messageInput.disabled = !showEngraving || !hasCharmSelection;

      if (messageInput.disabled) {
        messageInput.value = '';
      }
    }

    const message = this.#messageValue;
    const messageIsValid = this.#messageIsValid;
    const totalCents = [selectedCharmButton, selectedGemstoneButton].reduce((sum, button) => {
      if (!button) return sum;
      return sum + Number(button.dataset.priceCents || 0);
    }, 0);
    const hasSelection = Boolean(selectedCharmId || selectedGemstoneId);
    const canSubmit = this.dataset.baseCanAddToCart === 'true' && hasSelection && messageIsValid;

    const charmInput = this.#form?.querySelector('[data-personalisation-selected-charm-id]');
    const gemstoneInput = this.#form?.querySelector('[data-personalisation-selected-gemstone-id]');
    const messageHiddenInput = this.#form?.querySelector('[data-personalisation-message]');

    if (charmInput instanceof HTMLInputElement) charmInput.value = selectedCharmId;
    if (gemstoneInput instanceof HTMLInputElement) gemstoneInput.value = selectedGemstoneId;
    if (messageHiddenInput instanceof HTMLInputElement) messageHiddenInput.value = message;

    if (this.refs.messageCount) {
      this.refs.messageCount.textContent = String(messageInput?.value?.length ?? 0);
    }

    if (messageError) {
      messageError.classList.toggle('hidden', messageIsValid);
    }

    const submitPrice = this.querySelector('[ref="submitPrice"]');
    if (submitPrice) {
      submitPrice.textContent = this.#formatMoney(totalCents);
    }

    const submitButton = this.querySelector('[data-personalisation-submit]');
    if (submitButton instanceof HTMLButtonElement) {
      submitButton.disabled = !canSubmit;
    }

    const previewButton = selectedCharmButton || selectedGemstoneButton;
    this.#renderPreview(
      previewButton?.dataset.previewImage || this.dataset.defaultPreviewImage || '',
      previewButton?.dataset.previewAlt || this.dataset.defaultPreviewAlt || ''
    );
  }

  #renderPreview(src, alt) {
    const previewImage = this.refs.previewImage;

    if (!(previewImage instanceof HTMLImageElement)) return;

    if (src) {
      previewImage.src = src;
      previewImage.alt = alt;
    }
  }

  #formatMoney(cents) {
    const currency = this.dataset.currencyCode || 'GBP';

    try {
      return new Intl.NumberFormat(document.documentElement.lang || 'en-GB', {
        style: 'currency',
        currency,
      }).format(cents / 100);
    } catch (_) {
      return `£${(cents / 100).toFixed(2)}`;
    }
  }
}

if (!customElements.get('personalisation-component')) {
  customElements.define('personalisation-component', PersonalisationComponent);
}
